/**
 * Handles a promise in the foreground, returning the result and error as a
 * tuple. The first argument is the result; the second is the error. If one
 * is present, the other is not! Very simple, scalable way to handle promises
 * without a lot of nested try/catch.
 *
 * ⚠️ This function will NEVER throw
 *
 * @example
 * const [result, error] = await handleForegroundPromise(async () => {
 *   return 42;
 * }); // [42, undefined]
 *
 * @example
 * const [result, error] = await handleForegroundPromise(async () => {
 *   throw new Error("async error");
 * }); // [undefined, Error("async error")]
 *
 * @example
 * handleForegroundPromise(async () => {
 *   // do something cool
 * }).then(([result, error]) => {
 *   Then, go on your merry way
 * })
 */
export async function handleForegroundPromise<T>(
  fn: () => T | Promise<T>
): Promise<[T, undefined] | [undefined, Error]> {
  try {
    const result = await fn();
    return [result, undefined];
  } catch (error) {
    return [undefined, error];
  }
}
